/**
 * WordTraceLayer — Runespring unified-field trace gesture overlay.
 *
 * Phase 31 (RUNE-30) rewrite.
 *
 * Pointer-driven SVG path overlay that captures the player's word trace
 * across the unified Runespring field. The cell-letter map is built directly
 * from `playerView.board[]` (Phase 23/Phase 30 unified shape); Hollow cells
 * (Phase 26 — consumed Anchors) are excluded from the trace letter map AND,
 * when traced directly ONTO by the pointer, trigger a 4-channel compound
 * rejection per UI-SPEC §4. (ARCA-630 reverses ARCA-617: a diagonal may now
 * squeeze PAST a Hollow corner — only landing on a Hollow is blocked.)
 *
 *   1. Red flash on the offending cell (DOM `data-cell-flash="invalid"`
 *      affordance, 200ms ease-out)
 *   2. Last-segment path flash on the SVG polyline
 *   3. `navigator.vibrate(50)` — gated on capability check AND `!reducedMotion`
 *      (WCAG 2.3.3; vibration is motion)
 *   4. Screen-reader assertive announce via `useAnnounce` — Hollow rejection
 *      uses `match.error.invalid_trace_hollow`; a Tongue-Tie frozen cell
 *      (ARCA-142) uses `match.error.invalid_trace_frozen`; a non-Hollow,
 *      non-frozen adjacency failure uses the generic `match.error.invalid_trace`
 *
 * The compound clears the in-flight path back to []. All three rejection
 * paths (tracing onto a Hollow, a Tongue-Tie frozen cell, and a non-adjacent
 * jump) converge on the same `abortTrace(offendingCell, reason)` helper —
 * only the i18n message id (and the rejection SFX) differ.
 *
 * Score feedback (ARCA-597 — retires the floating orange ScorePill that
 * covered the top of the grid): each traced cell paints a small floating
 * "+N" combat text anchored at the cell center INSIDE the trace SVG (already
 * in grid coordinates), so the rest of the board stays visible during a drag.
 * The values come straight from `buildScorePillBreakdown(...).cells[i].value`
 * (the same pure formatter the staged-word chip uses) — scoring is never
 * recomputed here (DEC-021: display-only; the backend is authoritative on the
 * submitted reward). The combat text is purely visual: the whole SVG is
 * aria-hidden, so the single coherent score utterance is owned by the live
 * running-total chip (StagedWordChip) near Submit, not this overlay. The
 * on-lift valid/invalid verdict flash (ScorePillVerdict) likewise moved off
 * the board to the chip area (MatchScreen).
 *
 * DEC-021 (server authority): adjacency + reuse + Hollow checks in this
 * layer are ergonomic-only — the backend's `validatePath` is the canonical
 * authority on submitted word legality. A tampered path[] reaches the
 * server's 400 VALIDATION_FAILED path regardless of what this overlay
 * decided.
 */

import { useStore } from '@nanostores/react'
import {
  type JSX,
  useCallback,
  useDeferredValue,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useIntl } from 'react-intl'
import { playSfx } from '../../game/audio/useAudio'
import { boardRows, cellIdxToScreenRow } from '../../game/layers/board-layout'
import type { PlayerView } from '../../game/PlayerViewSchema'
import { $reducedMotion } from '../../stores/motion'
import { $turnBuilder, setPhaseA } from '../../stores/turnBuilder'
import { useAnnounce } from '../components/useAnnounce'
import { buildScorePillBreakdown } from './ScorePillFormat'

export interface WordTraceLayerProps {
  readonly playerView: PlayerView
  readonly onComplete: (path: number[]) => void
  /**
   * When false, the layer keeps `pointer-events: none` and ignores all
   * pointer input. Default is true so existing tests (which mount the
   * layer in isolation without a $turnBuilder Phase A) keep working.
   * MatchScreen passes `enabled={phaseA.kind === 'word'}`.
   */
  readonly enabled?: boolean
}

const COLS = 6

/**
 * Frozen empty sentinel so the no-staged-word render path is referentially
 * stable (no fresh `[]` each render when Phase A is not a word).
 */
const EMPTY_PATH: ReadonlyArray<number> = Object.freeze([])

function rowOf(i: number): number {
  return Math.floor(i / COLS)
}
function colOf(i: number): number {
  return i % COLS
}

/** Unified-field adjacency: orthogonal + diagonal, no same-cell. */
function isAdjacent(a: number, b: number): boolean {
  const dr = Math.abs(rowOf(a) - rowOf(b))
  const dc = Math.abs(colOf(a) - colOf(b))
  return dr <= 1 && dc <= 1 && dr + dc > 0
}

/**
 * Cell idx → screen (row, col) center, mirroring MatchGrid's
 * `screenToCellIdx` so the trace polyline lands on the same visual
 * position the cell renders at (slot=0 boards are flipped vertically).
 */
function cellIdxToScreenCenter(
  idx: number,
  yourSlot: 0 | 1,
  rows: number,
): { x: number; y: number } {
  const screenRow = cellIdxToScreenRow(idx, yourSlot, rows)
  const screenCol = colOf(idx)
  return { x: screenCol + 0.5, y: screenRow + 0.5 }
}

function pathToPolylinePoints(
  path: ReadonlyArray<number>,
  yourSlot: 0 | 1,
  rows: number,
): string {
  return path
    .map((i) => {
      const { x, y } = cellIdxToScreenCenter(i, yourSlot, rows)
      return `${x},${y}`
    })
    .join(' ')
}

function buildLetterMap(playerView: PlayerView): Map<number, string> {
  // Runespring unified field — letters live in `board[]`. Cells with
  // `letter===null` (Hollows, empty cells) are skipped so the trace path
  // adjacency is correctly broken at those positions. The explicit Hollow
  // detection in onPointerMove (P31) fires the 4-channel compound BEFORE
  // this silent map lookup would otherwise reject the cell.
  const map = new Map<number, string>()
  for (const cell of playerView.board) {
    if (cell.letter !== null && !cell.is_hollow) {
      map.set(cell.cell_idx, cell.letter)
    }
  }
  return map
}

/** Brief red-flash on the offending cell (Pitfall feedback locked in CONTEXT.md). */
function flashInvalidCell(cellEl: HTMLElement | null): void {
  if (cellEl === null) return
  cellEl.dataset.cellFlash = 'invalid'
  window.setTimeout(() => {
    if (cellEl.dataset.cellFlash === 'invalid') delete cellEl.dataset.cellFlash
  }, 200)
}

export function WordTraceLayer({
  playerView,
  onComplete,
  enabled = true,
}: WordTraceLayerProps): JSX.Element {
  const intl = useIntl()
  const reducedMotion = useStore($reducedMotion)
  // ARCA-121 Bug A — subscribe to the staged turn-builder so the ribbon +
  // ScorePill survive pointer-up. The local `path` state is cleared on lift
  // (see onPointerUp), but the chosen word persists in $turnBuilder.phaseA
  // until the player submits, clears, or starts a new trace. Mirrors MatchGrid,
  // which already drives `data-cell-in-trace` off this same staged path.
  const builder = useStore($turnBuilder)
  const announce = useAnnounce()
  const layerRef = useRef<HTMLDivElement>(null)
  const [path, setPath] = useState<ReadonlyArray<number>>([])
  // Mutable ref shadow so async DOM callbacks (red-flash setTimeout,
  // move/up handlers fired in quick succession) read the latest value
  // without waiting on the next React re-render.
  const pathRef = useRef<ReadonlyArray<number>>([])
  const draggingRef = useRef<boolean>(false)

  const letterByCell = useMemo(() => buildLetterMap(playerView), [playerView])
  // Phase 31 — fast cell lookup for Hollow detection at pointermove.
  const cellByIdx = useMemo(() => {
    const m = new Map<number, PlayerView['board'][number]>()
    for (const c of playerView.board) m.set(c.cell_idx, c)
    return m
  }, [playerView])
  const yourSlot = (playerView.your_slot ?? 0) as 0 | 1
  // Live row count — the SVG viewBox + polyline centers must track the grown
  // field (36→48→60 cells) so the trace stays aligned with the canvas/grid.
  const rows = boardRows(playerView.board.length)
  // Live board length for the hit-test upper bound — the Runespring field grows
  // 36→48→60, so a fixed `idx > 35` cap silently dropped traces over grown-board
  // cells. Track via a ref so the empty-dep `cellAtPoint` stays current.
  const boardLenRef = useRef(playerView.board.length)
  boardLenRef.current = playerView.board.length

  // MatchScreen drives this prop from `phaseA.kind === 'word'`; tests
  // mount with the default of `true`.
  const traceActive = enabled

  /**
   * Hit-test: resolve a viewport (x, y) pair into a `[data-cell-idx]`
   * cell index, or null if the pointer is not over any cell. Defensive
   * against `elementFromPoint` returning null and against `closest()`
   * hitting a non-numeric `data-cell-idx`.
   *
   * The layer temporarily disables its own pointer-events for the
   * elementFromPoint call so that the hit-test resolves to the cell
   * button BELOW it in the DOM stack — without this trick the hit would
   * always land on the layer itself (which has no `data-cell-idx`).
   *
   * `strict` enforces an inner-zone test: the pointer must be inside the
   * cell's central ~50% (both axes) to register. This is what stops a
   * diagonal drag from grazing the orthogonal neighbour it has to cross
   * on the way (the "L-shape" artifact). On pointerdown we use strict=
   * false so users can start a trace from anywhere inside a cell.
   */
  const cellAtPoint = useCallback(
    (
      x: number,
      y: number,
      strict: boolean,
    ): {
      idx: number
      el: HTMLElement
    } | null => {
      const layer = layerRef.current
      const prev = layer?.style.pointerEvents
      if (layer) layer.style.pointerEvents = 'none'
      try {
        const el = document.elementFromPoint(x, y) as HTMLElement | null
        if (el === null) return null
        const cell = el.closest<HTMLElement>('[data-cell-idx]')
        if (cell === null) return null
        const raw = cell.dataset.cellIdx
        if (raw === undefined) return null
        const idx = Number(raw)
        if (!Number.isInteger(idx) || idx < 0 || idx >= boardLenRef.current) {
          return null
        }
        if (strict) {
          const rect = cell.getBoundingClientRect()
          // happy-dom returns 0-sized rects; skip the inner-zone gate then
          // so unit tests don't all collapse to null.
          if (rect.width > 0 && rect.height > 0) {
            const dx = Math.abs(x - (rect.left + rect.width / 2)) / rect.width
            const dy = Math.abs(y - (rect.top + rect.height / 2)) / rect.height
            // Inner 50% box on each axis (|d| <= 0.25 from center). Tight
            // enough that diagonal drags skip the orthogonal neighbour
            // they cross, loose enough that intentional moves to a cell
            // always register before the pointer leaves it.
            if (dx > 0.25 || dy > 0.25) return null
          }
        }
        return { idx, el: cell }
      } finally {
        if (layer) layer.style.pointerEvents = prev ?? ''
      }
    },
    [],
  )

  /**
   * Commit the in-progress path to both local state AND $turnBuilder so
   * MatchGrid cell highlights / scale animations track the drag in real
   * time (without waiting for pointerup).
   */
  const commitLivePath = useCallback(
    (next: ReadonlyArray<number>) => {
      // INT-02: the signature rising trace whisper. Fire ONLY when the path
      // GROWS by a letter (start tap or a new adjacent cell) — never on a
      // same-length recommit — so the cue climbs in lock-step with the trace.
      // opts.index drives the TRACE_LADDER pitch climb (mute-gated, plays
      // regardless of reduced motion per the audio policy).
      if (next.length > pathRef.current.length) {
        playSfx('trace-step', { index: next.length - 1 })
      }
      pathRef.current = next
      setPath(next)
      const word = next.map((j) => letterByCell.get(j) ?? '').join('')
      setPhaseA({ kind: 'word', word, path: [...next] })
    },
    [letterByCell],
  )

  /**
   * Phase 31 (RUNE-30) — abort the in-flight trace and fire the 4-channel
   * compound rejection feedback. Both rejection causes (Hollow break-
   * adjacency and non-adjacent jump) converge on this helper; the i18n
   * message id is the only difference.
   *
   * Channels:
   *   1. flashInvalidCell — DOM data-cell-flash="invalid" for 200ms.
   *   2. navigator.vibrate(50) — gated on capability + !reducedMotion
   *      (vibration is motion per WCAG 2.3.3).
   *   3. announce(message, 'assertive') — screen-reader compound feedback.
   *   4. Path clear — back to [] in both local state and $turnBuilder.
   */
  const abortTrace = useCallback(
    (
      offendingCell: HTMLElement | null,
      reason: 'hollow' | 'non_adjacent' | 'frozen',
    ): void => {
      // Channel 3 — capability-gated haptic. Pitfall 3 lock: keep the full
      // 4-clause feature detection (typeof navigator, 'vibrate' in navigator,
      // typeof navigator.vibrate === 'function'). Phase 31 adds the
      // !reducedMotion gate.
      if (
        typeof navigator !== 'undefined' &&
        'vibrate' in navigator &&
        typeof navigator.vibrate === 'function' &&
        !reducedMotion
      ) {
        navigator.vibrate(50)
      }
      // INT-02: audible rejection cue (mute-gated; not RM-gated). A Hollow
      // break uses the dedicated dry hollow-reject thunk; a frozen
      // (Tongue-Tie) or non-adjacent / cell-reuse jump uses the softer
      // ui-error tick.
      playSfx(reason === 'hollow' ? 'hollow-reject' : 'ui-error')
      // Channel 1 — DOM red-flash on the offending cell.
      flashInvalidCell(offendingCell)
      // Channel 4 — screen-reader announce. Hollow and frozen (Tongue-Tie —
      // ARCA-142) each use their own specific message so a SR user hears WHY
      // the rune is off-limits; non-adjacent falls back to the generic key.
      const messageId =
        reason === 'hollow'
          ? 'match.error.invalid_trace_hollow'
          : reason === 'frozen'
            ? 'match.error.invalid_trace_frozen'
            : 'match.error.invalid_trace'
      announce(intl.formatMessage({ id: messageId }), 'assertive')
      // Channel 2 — SVG path clear (last-segment flash is implicit because
      // the polyline points list shrinks back to []; the next render frame
      // shows the empty polyline).
      pathRef.current = []
      setPath([])
      setPhaseA({ kind: 'word', word: '', path: [] })
    },
    [reducedMotion, announce, intl],
  )

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!traceActive) return
      try {
        e.currentTarget.setPointerCapture(e.pointerId)
      } catch {
        // happy-dom / older browsers — gesture still works via bubbling.
      }
      const hit = cellAtPoint(e.clientX, e.clientY, /* strict */ false)
      if (hit === null) return
      // Phase 31 — refuse to START a trace on a Hollow cell.
      const startCell = cellByIdx.get(hit.idx)
      if (startCell?.is_hollow === true) {
        abortTrace(hit.el, 'hollow')
        return
      }
      // ARCA-142 — refuse to START a trace on a Tongue-Tie frozen cell. The
      // server 409s any word path through a frozen cell
      // (validation/word.ts `cell_frozen_by_tongue_tie`); rejecting here keeps
      // that path from ever reaching submit. Parallel to the Hollow guard.
      if (startCell?.is_frozen === true) {
        abortTrace(hit.el, 'frozen')
        return
      }
      draggingRef.current = true
      commitLivePath([hit.idx])
    },
    [cellAtPoint, cellByIdx, abortTrace, commitLivePath, traceActive],
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return
      const cur = pathRef.current
      const last = cur.length === 0 ? null : cur[cur.length - 1]
      if (last === undefined || last === null) return
      const hit = cellAtPoint(e.clientX, e.clientY, /* strict */ true)
      if (hit === null) return
      if (hit.idx === last) return
      // Phase 31 — Hollow detection. The cell-letter map already excludes
      // Hollows; but checking the explicit is_hollow flag here lets the
      // 4-channel compound fire with the more-specific
      // `match.error.invalid_trace_hollow` message BEFORE the generic
      // non-adjacency / cell-reuse path runs.
      const targetCell = cellByIdx.get(hit.idx)
      if (targetCell?.is_hollow === true) {
        draggingRef.current = false
        abortTrace(hit.el, 'hollow')
        return
      }
      // ARCA-142 — reject dragging the trace INTO a Tongue-Tie frozen cell,
      // mirroring the Hollow guard above. Fires the frozen-specific
      // 4-channel compound (distinct announce) before the generic
      // non-adjacency / cell-reuse path runs.
      if (targetCell?.is_frozen === true) {
        draggingRef.current = false
        abortTrace(hit.el, 'frozen')
        return
      }
      if (cur.includes(hit.idx)) {
        draggingRef.current = false
        abortTrace(hit.el, 'non_adjacent')
        return
      }
      if (!isAdjacent(last, hit.idx)) {
        draggingRef.current = false
        abortTrace(hit.el, 'non_adjacent')
        return
      }
      // ARCA-630 — a diagonal step may squeeze past a Hollow corner (reverses
      // ARCA-617). Only tracing directly ONTO a Hollow is blocked, handled by
      // the is_hollow guard above; there is no longer a corner-squeeze guard.
      commitLivePath([...cur, hit.idx])
    },
    [cellAtPoint, cellByIdx, abortTrace, commitLivePath],
  )

  const onPointerUp = useCallback(() => {
    const wasDragging = draggingRef.current
    draggingRef.current = false
    const final = pathRef.current
    pathRef.current = []
    setPath([])
    if (!wasDragging) return
    if (final.length >= 2) {
      // SCORE-04 — the on-lift verdict flash now fires in MatchScreen
      // (relocated off the board to the chip area, ARCA-597). onComplete is
      // the single hand-off: MatchScreen stages the word AND flashes the
      // optimistic valid/invalid verdict from the committed path length.
      onComplete([...final])
    } else {
      // Single-tap without drag — clear so we don't leave a stuck highlight.
      setPhaseA({ kind: 'word', word: '', path: [] })
    }
  }, [onComplete])

  const onPointerCancel = useCallback(() => {
    const wasDragging = draggingRef.current
    draggingRef.current = false
    pathRef.current = []
    setPath([])
    if (wasDragging) {
      setPhaseA({ kind: 'word', word: '', path: [] })
    }
  }, [])

  const ariaLabel = intl.formatMessage({
    id: 'match.grid.word_trace.aria_label',
    defaultMessage: 'Word trace surface, drag across adjacent letters',
  })

  // ARCA-121 Bug A — the path the overlay PAINTS. During an active drag the
  // local `path` leads (it updates per pointermove; commitLivePath keeps the
  // store identity-synced). After lift `draggingRef` is false and the staged
  // store path takes over, so the ribbon + ScorePill stay visible as the
  // player's confirmation of the word they have staged — and tear down the
  // frame Phase A is cleared (submit / clearPhaseA / switch to skip).
  const stagedPath: ReadonlyArray<number> =
    builder.phaseA?.kind === 'word' ? builder.phaseA.path : EMPTY_PATH
  const displayPath = draggingRef.current ? path : stagedPath

  // ARCA-597 — per-cell combat text source. Defer the painted path so the
  // breakdown re-formats at most once per idle frame (~16ms cadence the
  // retired ScorePill used; Pitfall 8), not on every 60Hz pointermove. The
  // formatter is pure + O(path); each `cells[i]` carries the source cellIdx so
  // each `+value` anchors at that cell's center without any extra math.
  const deferredDisplayPath = useDeferredValue(displayPath)
  const combatCells = useMemo(
    () =>
      buildScorePillBreakdown(deferredDisplayPath, playerView, yourSlot, intl)
        .cells,
    [deferredDisplayPath, playerView, yourSlot, intl],
  )

  // Trace-head center (prototype white head dot). Last cell in the painted
  // path; null when the path is empty so the dot only paints during/after a
  // trace.
  const headIdx =
    displayPath.length > 0 ? displayPath[displayPath.length - 1] : undefined
  const headCenter =
    headIdx !== undefined
      ? cellIdxToScreenCenter(headIdx, yourSlot, rows)
      : null

  return (
    <div
      ref={layerRef}
      role="application"
      aria-label={ariaLabel}
      data-screen-region="word-trace-layer"
      data-trace-active={displayPath.length > 0 ? 'true' : 'false'}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      style={{
        position: 'absolute',
        inset: 0,
        touchAction: 'none',
        // Only intercept events when Phase A 'word' is active so MatchGrid
        // (Phase B move/empower clicks) and PieceDragLayer keep working
        // in other phases. Tests that mount the layer in isolation rely
        // on the `enabled` prop default of `true`.
        pointerEvents: traceActive ? 'auto' : 'none',
        // Sit above MatchGrid; explicit z-index avoids ordering surprises
        // if MatchScreen ever rearranges sibling render order.
        zIndex: 3,
      }}
    >
      <svg
        aria-hidden="true"
        viewBox={`0 0 ${COLS} ${rows}`}
        preserveAspectRatio="none"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          overflow: 'visible',
        }}
      >
        {/* Prototype trace ribbon (MatchScreen.jsx trace SVG): a warm ember
            gradient stroke (--color-cta-primary-top → --color-player-a), a soft
            feGaussianBlur glow, an animated dash-flow on the top stroke, and a
            white head dot at the trace head. Stops/strokes/dot are folk-token
            driven (no hex).

            We keep `vectorEffect="non-scaling-stroke"` so widths stay in screen
            px under the 0..6 viewBox (a drop-shadow / large user-space blur
            would balloon to ~⅓ of the board). The glow filter uses
            userSpaceOnUse units so a perfectly straight (zero-bbox) trace still
            renders, and a small stdDeviation keeps the bloom subtle.

            REDUCED MOTION: the dash-flow is the ONLY animated part. It is gated
            on the $reducedMotion store (drops the `rg-trace-flow` class +
            stroke-dasharray) AND the .rg-trace-flow CSS dual-gate
            (prefers-reduced-motion + [data-motion="reduced"]) in pieces.css.
            Under reduced motion the top stroke renders STATICALLY — a solid
            gradient stroke, no dash, no infinite animation. The gradient + glow
            + head dot are static and need no gate. */}
        <defs>
          <linearGradient id="rg-trace-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="var(--color-cta-primary-top)" />
            <stop offset="1" stopColor="var(--color-player-a)" />
          </linearGradient>
          <filter
            id="rg-trace-glow"
            filterUnits="userSpaceOnUse"
            x={-1}
            y={-1}
            width={COLS + 2}
            height={rows + 2}
          >
            <feGaussianBlur stdDeviation="0.06" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {/* Wide warm-gradient glow halo painted first (the glowing connector
            ribbon). Static — no reduced-motion gate needed. */}
        <polyline
          points={pathToPolylinePoints(displayPath, yourSlot, rows)}
          stroke="url(#rg-trace-grad)"
          strokeWidth={13}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          vectorEffect="non-scaling-stroke"
          opacity={0.3}
        />
        {/* Inkwell core ribbon — shows through the dash gaps as a folk ink
            line, giving the flowing ember stroke depth. Static. */}
        <polyline
          points={pathToPolylinePoints(displayPath, yourSlot, rows)}
          stroke="var(--color-inkwell)"
          strokeWidth={8}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          vectorEffect="non-scaling-stroke"
          opacity={0.85}
        />
        {/* Top ember stroke — warm gradient + soft glow. Animated dash-flow
            ONLY when motion is allowed; a solid static stroke under reduced
            motion. */}
        <polyline
          className={reducedMotion ? undefined : 'rg-trace-flow'}
          points={pathToPolylinePoints(displayPath, yourSlot, rows)}
          stroke="url(#rg-trace-grad)"
          strokeWidth={5}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          vectorEffect="non-scaling-stroke"
          filter="url(#rg-trace-glow)"
          strokeDasharray={reducedMotion ? undefined : '3 15'}
        />
        {/* White head dot at the trace head (prototype white circle, ember
            rim). Static — paints only while a trace exists. */}
        {headCenter !== null ? (
          <circle
            cx={headCenter.x}
            cy={headCenter.y}
            r={0.16}
            fill="var(--color-on-action)"
            stroke="var(--color-player-a)"
            strokeWidth={3}
            vectorEffect="non-scaling-stroke"
          />
        ) : null}
        {/* ARCA-597 — per-cell combat text. One floating "+N" per traced cell,
            anchored at the cell center (col+0.5, row+0.5 — same grid coords as
            the polyline) so the score never covers the board the way the old
            top-of-grid ScorePill did. fontSize/x/y are in the 0..COLS × 0..rows
            user space (cells are square, so the non-uniform preserveAspectRatio
            doesn't distort the glyphs); the cream fill + inkwell stroke (painted
            under via paint-order) keep the number legible over the ember ribbon.
            Decorative: the parent <svg> is aria-hidden, so the single SR score
            utterance stays with the running-total chip (AC#5). Motion: a slight
            rise + fade-in on entry (.rg-combat-text); under reduced motion the
            class is dropped → the value appears static (AC#4), mirroring the
            .rg-trace-flow JS gate above. */}
        {combatCells.map((c) => {
          const { x, y } = cellIdxToScreenCenter(c.cellIdx, yourSlot, rows)
          return (
            <text
              key={c.key}
              x={x}
              y={y}
              data-combat-text={c.value}
              className={reducedMotion ? undefined : 'rg-combat-text'}
              textAnchor="middle"
              dominantBaseline="central"
              fontFamily="var(--font-family-display)"
              fontWeight={700}
              fontSize={0.42}
              fill="var(--color-on-action)"
              stroke="var(--color-inkwell)"
              strokeWidth={3}
              paintOrder="stroke"
              vectorEffect="non-scaling-stroke"
              style={{ pointerEvents: 'none' }}
            >
              {`+${c.value}`}
            </text>
          )
        })}
      </svg>
    </div>
  )
}
